import React, { useCallback, useEffect, useState } from 'react';
import { CheckCircle2, ExternalLink, Link2, Loader2, Unplug } from 'lucide-react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  authorizeContactApp,
  bootstrapContactSpace,
  disconnectContactApp,
  getContactSpace,
} from '../../api/contactSpace';
import { getApiErrorMessage } from '../../utils/errors';

function SpaceFrame({ agentName, children }) {
  return (
    <div className="min-h-screen bg-slate-50">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-3xl items-center justify-between px-5 py-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-forge-600">
              Seu espaço
            </p>
            <p className="mt-0.5 font-semibold text-slate-900">
              {agentName || 'Carregando…'}
            </p>
          </div>
          <span className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-medium text-emerald-700">
            Acesso privado
          </span>
        </div>
      </header>
      <main className="mx-auto max-w-3xl px-5 py-8">{children}</main>
    </div>
  );
}

function LoadingState({ text = 'Abrindo seu espaço…' }) {
  return (
    <SpaceFrame>
      <div className="flex min-h-[45vh] flex-col items-center justify-center text-center">
        <Loader2 className="h-7 w-7 animate-spin text-forge-600" />
        <p className="mt-3 text-sm text-slate-600">{text}</p>
      </div>
    </SpaceFrame>
  );
}

export function ContactSpaceEntry() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    bootstrapContactSpace(token)
      .then(() => {
        if (active) navigate('/espaco/conexoes', { replace: true });
      })
      .catch((err) => {
        if (active) {
          setError(
            getApiErrorMessage(
              err,
              'Este link não é mais válido. Peça um novo link na conversa.'
            )
          );
        }
      });
    return () => {
      active = false;
    };
  }, [navigate, token]);

  if (!error) return <LoadingState />;
  return (
    <SpaceFrame>
      <div className="mx-auto mt-12 max-w-md rounded-2xl border border-red-200 bg-white p-6 text-center shadow-sm">
        <p className="font-semibold text-slate-900">Não foi possível abrir o espaço</p>
        <p className="mt-2 text-sm leading-6 text-slate-600">{error}</p>
      </div>
    </SpaceFrame>
  );
}

function AppCard({ app, onChanged }) {
  const [busy, setBusy] = useState(false);
  const connected = app.status === 'connected';

  const handleConnect = async () => {
    setBusy(true);
    try {
      const result = await authorizeContactApp(app.id);
      window.location.assign(result.authorization_url);
    } catch (err) {
      onChanged(getApiErrorMessage(err, 'Não foi possível iniciar a conexão.'));
      setBusy(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm(`Desconectar ${app.name}?`)) return;
    setBusy(true);
    try {
      await disconnectContactApp(app.id);
      await onChanged();
    } catch (err) {
      onChanged(getApiErrorMessage(err, 'Não foi possível desconectar o aplicativo.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <article className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex items-start gap-4">
        <div className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-forge-50 text-forge-700">
          {app.icon_url ? (
            <img src={app.icon_url} alt="" className="h-full w-full object-cover" />
          ) : (
            <Link2 className="h-6 w-6" />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="font-semibold text-slate-900">{app.name}</h2>
            {connected && (
              <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700">
                <CheckCircle2 className="h-3.5 w-3.5" /> Conectado
              </span>
            )}
          </div>
          {app.description && (
            <p className="mt-1 text-sm leading-6 text-slate-600">{app.description}</p>
          )}
          {connected && app.external_account_label && (
            <p className="mt-2 text-xs text-slate-500">{app.external_account_label}</p>
          )}
          <button
            type="button"
            disabled={busy}
            onClick={connected ? handleDisconnect : handleConnect}
            className={
              connected
                ? 'mt-4 inline-flex items-center gap-2 text-sm font-medium text-slate-600 hover:text-red-600'
                : 'btn-primary mt-4 inline-flex items-center gap-2 px-4 py-2 text-sm'
            }
          >
            {busy ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : connected ? (
              <Unplug className="h-4 w-4" />
            ) : (
              <ExternalLink className="h-4 w-4" />
            )}
            {connected ? 'Desconectar' : 'Conectar'}
          </button>
        </div>
      </div>
    </article>
  );
}

export default function ContactSpaceConnections() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [space, setSpace] = useState(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const load = useCallback(async (message) => {
    if (typeof message === 'string') {
      setError(message);
      return;
    }
    try {
      setSpace(await getContactSpace());
      setError('');
    } catch (err) {
      setError(
        getApiErrorMessage(err, 'Sua sessão expirou. Peça um novo link na conversa.')
      );
    }
  }, []);

  useEffect(() => {
    const connection = searchParams.get('connection');
    if (connection === 'connected') setNotice('Aplicativo conectado com sucesso.');
    if (connection === 'error') {
      setError(searchParams.get('message') || 'Não foi possível conectar o aplicativo.');
    }
    if (connection) setSearchParams({}, { replace: true });
    load();
  }, [load, searchParams, setSearchParams]);

  if (!space && !error) return <LoadingState text="Carregando suas conexões…" />;

  return (
    <SpaceFrame agentName={space?.agent?.name}>
      <div className="mb-7">
        <h1 className="text-2xl font-bold text-slate-900">Aplicativos conectados</h1>
        <p className="mt-2 max-w-xl text-sm leading-6 text-slate-600">
          Você decide quais aplicativos podem ser usados para personalizar sua
          experiência. É possível desconectar a qualquer momento.
        </p>
      </div>

      {notice && (
        <div className="mb-5 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
          {notice}
        </div>
      )}
      {error && (
        <div className="mb-5 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}
      {space?.apps?.length ? (
        <div className="space-y-4">
          {space.apps.map((app) => (
            <AppCard key={app.id} app={app} onChanged={load} />
          ))}
        </div>
      ) : (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white px-6 py-10 text-center">
          <p className="font-medium text-slate-800">Nenhum aplicativo disponível agora</p>
          <p className="mt-2 text-sm text-slate-500">
            Quando houver uma integração para você, ela aparecerá aqui.
          </p>
        </div>
      )}
      <p className="mt-8 text-center text-xs text-slate-400">
        Seus dados de acesso são protegidos e nunca aparecem na conversa.
      </p>
    </SpaceFrame>
  );
}
